import EventEmitter from 'events';
import { useEffect, useState } from 'react';
import TypedEmitter from 'typed-emitter';
import {
  BlackjackArea as BlackjackAreaModel,
  BlackjackPlayer,
  BlackjackResult,
  Card,
} from '../types/CoveyTownSocket';

/**
 * The events that the BlackjackAreaController emits to subscribers. These events
 * are only ever emitted to local components (not to the townService).
 */
export type BlackjackAreaEvents = {
  isGamePlayingChange: (isGamePlaying: boolean) => void;
  blackjackPlayersChange: (newPlayers: BlackjackPlayer[]) => void;
  dealerHandChange: (newDealerHand: Card[]) => void;
  dealerCardFlip: (newFaceDown: boolean) => void;
  activeTurnPlayerChange: (newPlayerId: string) => void;
  isDealerDoneChange: (newIsDealerDone: boolean) => void;
};

/**
 * A BlackjackAreaController manages the local behavior of a blackjack area in the frontend,
 * implementing the logic to bridge between the townService's interpretation of blackjack areas and the
 * frontend's. The BlackjackAreaController emits events when the blackjack area changes.
 */
export default class BlackjackAreaController extends (EventEmitter as new () => TypedEmitter<BlackjackAreaEvents>) {
  private _blackjackPlayers: BlackjackPlayer[] = [];

  private _dealerHand: Card[] = [];

  private _id: string;

  private _isGamePlaying: boolean;

  private _dealerFirstCardDown = true;

  private _activeTurnPlayerId: string;

  private _maxPlayers = 2;

  private _isDealerDone = false;

  /**
   * Create a new BlackjackAreaController
   * @param id
   * @param topic
   */
  constructor(id: string, isPlaying: boolean, activeTurnPlayerId: string) {
    super();
    this._id = id;
    this._isGamePlaying = isPlaying;
    this._activeTurnPlayerId = activeTurnPlayerId;
  }

  /**
   * The ID of this blackjack area (read only)
   */
  get id() {
    return this._id;
  }

  /**
   * The maximum number of players in this blackjackarea
   */
  get maxPlayers(): number {
    return this._maxPlayers;
  }

  /**
   * The topic of the blackjack area. Changing the topic will emit a gameStatusChange event
   */
  set isGamePlaying(newStatus: boolean) {
    if (this._isGamePlaying !== newStatus) {
      this.emit('isGamePlayingChange', newStatus);
    }
    this._isGamePlaying = newStatus;
  }

  get isGamePlaying(): boolean {
    return this._isGamePlaying;
  }

  /**
   * The players of the blackjack area. Changing the players will emit a blackjackPlayersChange event
   */
  set blackjackPlayers(newBlackjackPlayers: BlackjackPlayer[]) {
    if (newBlackjackPlayers !== this.blackjackPlayers) {
      this.emit('blackjackPlayersChange', newBlackjackPlayers);
    }
    this._blackjackPlayers = newBlackjackPlayers;
  }

  get blackjackPlayers(): BlackjackPlayer[] {
    return this._blackjackPlayers;
  }

  /**
   * The dealer's hand of the blackjack area. Changing the dealer's hand will emit a dealerHandChange event
   */
  set dealerHand(hand: Card[]) {
    if (hand !== this._dealerHand) {
      this.emit('dealerHandChange', hand);
    }
    this._dealerHand = hand;
  }

  get dealerHand(): Card[] {
    return this._dealerHand;
  }

  /**
   * If the dealer's first card is facing down. Changing the status of dealer's first card will emit a dealerCardFlip event
   */
  set dealerFirstCardDown(faceDown: boolean) {
    if (this._dealerFirstCardDown !== faceDown) {
      this.emit('dealerCardFlip', faceDown);
    }
    this._dealerFirstCardDown = faceDown;
    if (!this._dealerFirstCardDown) {
      this.isDealerDone = true;
    }
  }

  get dealerFirstCardDown(): boolean {
    return this._dealerFirstCardDown;
  }

  /**
   * Id of the player whose turn is active. Changing this id will emit a activeTurnPlayerChange event
   */
  set activeTurnPlayerId(id: string) {
    if (this._activeTurnPlayerId !== id) {
      this.emit('activeTurnPlayerChange', id);
    }
    this._activeTurnPlayerId = id;
  }

  get activeTurnPlayerId(): string {
    return this._activeTurnPlayerId;
  }

  set isDealerDone(newIsDealerDone: boolean) {
    if (this._isDealerDone !== newIsDealerDone) {
      this.emit('isDealerDoneChange', newIsDealerDone);
    }
    this._isDealerDone = newIsDealerDone;
  }

  get isDealerDone(): boolean {
    return this._isDealerDone;
  }

  /**
   * Return a representation of this BlackjackAreaController that matches the
   * townService's representation and is suitable for transmitting over the network.
   */
  toBlackjackAreaModel(): BlackjackAreaModel {
    return {
      id: this.id,
      isGamePlaying: this.isGamePlaying,
      blackjackPlayers: this.blackjackPlayers,
      dealerHand: this.dealerHand,
      dealerFirstCardDown: this.dealerFirstCardDown,
      activeTurnPlayerId: this.activeTurnPlayerId,
    };
  }

  /**
   * Create a new BlackjackAreaController to match a given BlackjackAreaModel
   * @param blackjackAreaModel Blackjack area to represent
   * @param playerFinder A function that will return a list of PlayerController's
   *                     matching a list of Player ID's
   */
  static fromBlackjackAreaModel(blackjackAreaModel: BlackjackAreaModel): BlackjackAreaController {
    const ret = new BlackjackAreaController(
      blackjackAreaModel.id,
      blackjackAreaModel.isGamePlaying,
      blackjackAreaModel.activeTurnPlayerId,
    );
    ret.blackjackPlayers = blackjackAreaModel.blackjackPlayers;
    ret.dealerHand = blackjackAreaModel.dealerHand;
    return ret;
  }

  /**
   * Resets a BlackjackAreaController
   */
  public reset(): void {
    this.activeTurnPlayerId = '';
    this.blackjackPlayers = [];
    this.dealerFirstCardDown = true;
    this.isDealerDone = false;
    this.dealerHand = [];
  }
}

/**
 * A react hook to retrieve the game status of a BlackjackAreaController.
 *
 * This hook will re-render any components that use it when the game status changes.
 *
 * This hook relies on the TownControllerContext.
 * @param area blackjackarea controller
 */
export function useBlackjackAreaGameStatus(area: BlackjackAreaController): boolean {
  const [isGamePlaying, setIsGamePlaying] = useState(area.isGamePlaying);
  useEffect(() => {
    area.addListener('isGamePlayingChange', setIsGamePlaying);
    return () => {
      area.removeListener('isGamePlayingChange', setIsGamePlaying);
    };
  }, [area]);
  return isGamePlaying;
}

/**
 * A react hook to retrieve all blackjack players for this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @param blackjackArea blackjackarea controller
 * @returns a list of all blackjack players for this blackjack area
 */
export function useBlackjackPlayers(blackjackArea: BlackjackAreaController): BlackjackPlayer[] {
  const [players, setPlayers] = useState<BlackjackPlayer[]>(blackjackArea.blackjackPlayers);
  useEffect(() => {
    blackjackArea.addListener('blackjackPlayersChange', setPlayers);
    return () => {
      blackjackArea.removeListener('blackjackPlayersChange', setPlayers);
    };
  }, [blackjackArea, setPlayers]);
  return players;
}

/**
 * A react hook to retrieve the dealer's hand for this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @param blackjackArea blackjackarea controller
 * @returns the dealer's hand
 */
export function useBlackjackDealerHand(blackjackArea: BlackjackAreaController): Card[] {
  const [dealerHand, setDealerHand] = useState(blackjackArea.dealerHand);
  useEffect(() => {
    blackjackArea.addListener('dealerHandChange', setDealerHand);
    return () => {
      blackjackArea.removeListener('dealerHandChange', setDealerHand);
    };
  }, [blackjackArea]);
  return dealerHand;
}

/**
 * A react hook to retrieve if the dealer's first card is facing down for this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @param blackjackArea blackjackarea controller
 * @returns if the dealer's first card is facing down
 */
export function useBlackjackDealerFirstCardDown(blackjackArea: BlackjackAreaController): boolean {
  const [faceDown, setFaceDown] = useState(blackjackArea.dealerFirstCardDown);
  useEffect(() => {
    blackjackArea.addListener('dealerCardFlip', setFaceDown);
    return () => {
      blackjackArea.removeListener('dealerCardFlip', setFaceDown);
    };
  }, [blackjackArea]);
  return faceDown;
}

/**
 * A react hook to retrieve a blackjack player's game result in this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @param blackjackArea blackjackarea controller
 * @param playerId id of the player result we need
 * @returns the blackjack result of the player that has matching input player id
 */
export function useBlackjackPlayerResult(
  blackjackArea: BlackjackAreaController,
  playerId: string,
): BlackjackResult | undefined {
  const [playerResult, setPlayerResult] = useState(
    blackjackArea.blackjackPlayers.find(eachPlayer => eachPlayer.id === playerId)?.result,
  );
  useEffect(() => {
    const updater = (players: BlackjackPlayer[]) => {
      setPlayerResult(players.find(eachPlayer => eachPlayer.id === playerId)?.result);
    };
    blackjackArea.addListener('blackjackPlayersChange', updater);
    return () => {
      blackjackArea.removeListener('blackjackPlayersChange', updater);
    };
  }, [blackjackArea, playerId]);
  return playerResult;
}

/**
 * A react hook to retrieve a blackjack player in this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @param blackjackArea blackjackarea controller
 * @param playerId id of the player we need
 * @returns the player that has matching input player id
 */
export function useBlackjackPlayer(
  blackjackArea: BlackjackAreaController,
  playerId: string,
): BlackjackPlayer | undefined {
  const [player, setPlayer] = useState(
    blackjackArea.blackjackPlayers.find(eachPlayer => eachPlayer.id === playerId),
  );
  useEffect(() => {
    const updater = (players: BlackjackPlayer[]) => {
      setPlayer(players.find(eachPlayer => eachPlayer.id === playerId));
    };
    blackjackArea.addListener('blackjackPlayersChange', updater);
    return () => {
      blackjackArea.removeListener('blackjackPlayersChange', updater);
    };
  }, [blackjackArea, playerId]);
  return player;
}

/**
 * A react hook to retrieve the blackjack player whos turn is active for this blackjack area
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @returns the id of the player whose turn is active
 */
export function useBlackjackActiveTurnPlayerId(blackjackArea: BlackjackAreaController): string {
  const [playerId, setPlayerId] = useState(blackjackArea.activeTurnPlayerId);
  useEffect(() => {
    blackjackArea.addListener('activeTurnPlayerChange', setPlayerId);
    return () => {
      blackjackArea.removeListener('activeTurnPlayerChange', setPlayerId);
    };
  }, [blackjackArea]);
  return playerId;
}

/**
 * A react hook to retrieve if the blackjack dealer's turn has ended.
 *
 * This hook will cause components that use it to re-render when the active player change.
 *
 * This hook relies on the TownControllerContext.
 * @returns if the blackjack dealer's turn has ended.
 */
export function useBlackjackIsDealerDone(blackjackArea: BlackjackAreaController): boolean {
  const [isDealerDone, setDealerDone] = useState(blackjackArea.isDealerDone);
  useEffect(() => {
    blackjackArea.addListener('isDealerDoneChange', setDealerDone);
    return () => {
      blackjackArea.removeListener('isDealerDoneChange', setDealerDone);
    };
  }, [blackjackArea]);
  return isDealerDone;
}
